import { useCallback, useEffect, useRef, useState } from "react";
import { voiceSamplePath } from "@/constants/geminiVoices";

export interface VoiceSample {
  /** The voice whose sample is playing, or null. */
  playing: string | null;
  /** The voice whose sample last failed to play, until another is played or the samples stop. */
  failed: string | null;
  /** Plays a voice's sample, stopping any other; on the one playing, stops it. */
  toggle: (voiceId: string) => void;
  stop: () => void;
}

/** One voice sample at a time, stopped when the component that plays it goes away. */
export function useVoiceSample(): VoiceSample {
  const clip = useRef<HTMLAudioElement | null>(null);
  const [playing, setPlaying] = useState<string | null>(null);
  const [failed, setFailed] = useState<string | null>(null);

  // Stopping also clears a failure, so a dialog opened again starts without one.
  const stop = useCallback(() => {
    clip.current?.pause();
    clip.current = null;
    setPlaying(null);
    setFailed(null);
  }, []);

  const toggle = useCallback(
    (voiceId: string) => {
      const again = playing === voiceId;
      stop();
      if (again) return;
      const audio = new Audio(voiceSamplePath(voiceId));
      // A clip stopped or replaced may still report its end or a failure (pause() rejects its play()): only the
      // clip that is playing now changes what shows.
      const settle = (failure: boolean) => {
        if (clip.current !== audio) return;
        clip.current = null;
        setPlaying(null);
        if (failure) setFailed(voiceId);
      };
      audio.addEventListener("ended", () => settle(false));
      audio.addEventListener("error", () => settle(true));
      clip.current = audio;
      setPlaying(voiceId);
      audio.play().catch(() => settle(true));
    },
    [playing, stop],
  );

  useEffect(() => stop, [stop]);

  return { playing, failed, toggle, stop };
}
